import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BuildGameInterviewState,
  BuildGameInterviewStatusSummary,
} from '@volley/crucible-ipc-types/build-game-types';
import { isKnownBuildGameInterviewStatus } from '@volley/crucible-ipc-types/build-game-types';
import type { ProjectRef } from '@volley/crucible-ipc-types';
import { useCrucibleClient } from '../transport/context';
import { projectQueryKey } from '../transport/project-query-key';

/**
 * React Query hooks around the four `build-game:*` IPC channels
 * introduced in PR #1 of the interview-v2 rollout. Three call sites:
 *
 *   - `<InterviewInProgressBadge>` on Dashboard cards uses
 *     `useInterviewStatus` (lightweight summary).
 *   - The review-and-edit modal uses `useInterviewState` (full state).
 *   - The chat panel's `<ReviewAnswersButton>` uses
 *     `useInterviewStatus` to decide visibility.
 *
 * Cache policy mirrors `useTemplateUpdateCheck` from #135: short
 * `staleTime` (60s) plus `refetchOnMount: 'always'` and
 * `refetchOnWindowFocus: 'always'` so out-of-band CLI updates and
 * skill-side writes surface in the GUI immediately. The IPC handler
 * on the main side re-reads the state file on every call (Codex
 * round 4 §1) so refetches always see ground truth.
 */

const STATUS_OPTIONS = {
  staleTime: 60 * 1000,
  refetchOnMount: 'always',
  refetchOnWindowFocus: 'always',
  retry: false,
} as const;

const STATE_OPTIONS = STATUS_OPTIONS;

/**
 * Crucible #507 / #519: polling cadence for the in-flight statuses
 * driving the PhaseStrip empty-state and BuildPartialBanner. 5s strikes
 * a balance between snappy renderer updates when the SKILL flips the
 * status and not hammering main with IPC every render. Only applies
 * while the value matters (in-flight or partial); terminal statuses
 * leave the polling off.
 *
 * Exported so the unit suite can override it for fast tests.
 */
export const INTERVIEW_STATUS_LIVE_POLL_MS = 5_000;

/**
 * Crucible #507 / #519: returns the millisecond interval React Query
 * should poll at, OR `false` to disable polling. Live-poll while the
 * SKILL might flip the status under the user's nose; turn polling off
 * once the status is terminal so the cache stays at the last-known
 * value.
 *
 * The `null` case (state file does not yet exist) intentionally KEEPS
 * polling on. Codex Round-2 finding: the most common path is "user
 * opens the Workspace before typing `/build-game`" — the first IPC
 * returns `null`, then the SKILL writes the file moments later. If
 * polling were disabled on `null`, the PhaseStrip empty-state and
 * BuildPartialBanner would never wake up until the next focus or
 * remount. The cheap poll-and-find-still-null cycle is the price for
 * picking up that file-appearance edge.
 *
 * Contract note: the sole call site (`useInterviewStatus` below) passes
 * `query.state.data?.status ?? null`, which collapses both "no fetch
 * yet" (`data === undefined`) and "fetched but no file" (`data === null`)
 * to the single `null` input. The signature is narrowed to match so the
 * branch table is exhaustive against reachable inputs — anything
 * looser would tempt a dead `undefined` branch back into the helper.
 *
 * Pure — exported for unit pinning.
 */
export function refetchIntervalForStatus(
  status: BuildGameInterviewStatusSummary['status'] | null,
): number | false {
  // Terminal statuses don't mutate without user action, so polling adds
  // no value once we've seen one.
  if (status === 'built' || status === 'built-partial' || status === 'abandoned') {
    return false;
  }
  // All other states (null / 'in-progress' / 'complete' / 'dispatched'
  // / 'building') can flip under the user — keep polling.
  return INTERVIEW_STATUS_LIVE_POLL_MS;
}

export function refetchIntervalForState(state: BuildGameInterviewState | null): number | false {
  const status = state?.status;
  if (typeof status !== 'string' || !isKnownBuildGameInterviewStatus(status)) {
    return INTERVIEW_STATUS_LIVE_POLL_MS;
  }
  return refetchIntervalForStatus(status);
}

export function useInterviewStatus(
  gameId: string | undefined,
  opts?: { enabled?: boolean; project?: ProjectRef },
): {
  data: BuildGameInterviewStatusSummary | null | undefined;
  isLoading: boolean;
  refetch: () => void;
} {
  const client = useCrucibleClient();
  const externalEnabled = opts?.enabled ?? true;
  const query = useQuery<BuildGameInterviewStatusSummary | null>({
    queryKey: projectQueryKey(client, ['build-game:interview-status', gameId], opts?.project),
    queryFn: async () => {
      if (!gameId) throw new Error('gameId is required');
      return await client.invoke('build-game:interview-status', opts?.project ?? { gameId });
    },
    enabled: externalEnabled && typeof gameId === 'string' && gameId.length > 0,
    ...STATUS_OPTIONS,
    // Crucible #534 Bugbot LOW: `refetchInterval` MUST sit AFTER the
    // `...STATUS_OPTIONS` spread. Object-literal property order is
    // "last write wins" — if STATUS_OPTIONS ever grows a `refetchInterval`
    // (e.g. someone copies STATE_OPTIONS into STATUS_OPTIONS for parity),
    // an earlier-positioned callback here would be silently clobbered
    // and the #507 / #519 live-poll behaviour would vanish without any
    // type error. The unit suite pins this order via the
    // "options-order regression" test in __tests__/useBuildGameInterview.test.ts.
    refetchInterval: (query) => refetchIntervalForStatus(query.state.data?.status ?? null),
  });
  // React Query's `query.refetch` is a stable reference internally, but
  // wrapping it in an inline arrow rebuilds a fresh function on every
  // render — which breaks `useEffect`/`useCallback` deps for any
  // consumer that holds it in a deps array (#533 round-2 Codex MEDIUM).
  // Wrap once in `useCallback` so consumers can safely depend on it.
  const refetch = useCallback(() => {
    void query.refetch();
  }, [query.refetch]);
  return {
    data: query.data,
    isLoading: query.isLoading,
    refetch,
  };
}

export function useInterviewState(
  gameId: string | undefined,
  project?: ProjectRef,
): {
  data: BuildGameInterviewState | null | undefined;
  isLoading: boolean;
  refetch: () => void;
} {
  const client = useCrucibleClient();
  const query = useQuery<BuildGameInterviewState | null>({
    queryKey: projectQueryKey(client, ['build-game:get-interview-state', gameId], project),
    queryFn: async () => {
      if (!gameId) throw new Error('gameId is required');
      return await client.invoke('build-game:get-interview-state', { gameId });
    },
    enabled: typeof gameId === 'string' && gameId.length > 0,
    ...STATE_OPTIONS,
    refetchInterval: (query) => refetchIntervalForState(query.state.data ?? null),
  });
  // See `useInterviewStatus` above — same rationale (#533 round-2
  // Codex MEDIUM). The deploy-gate stale-state refetch effect in
  // `pages/Workspace.tsx` puts this function in its useEffect deps;
  // without `useCallback` the effect re-fires on every Workspace
  // render at phase 7 + non-built status, hammering the IPC.
  const refetch = useCallback(() => {
    void query.refetch();
  }, [query.refetch]);
  return {
    data: query.data,
    isLoading: query.isLoading,
    refetch,
  };
}

/**
 * Imperative helper for `<ReviewAnswersButton>` and the resume
 * panel's "Start over" option. Wraps `build-game:start-over` and
 * invalidates the React Query cache so the badge / modal disappear
 * immediately. The skill (PR #4) sees a missing state file on its
 * next preflight and starts fresh.
 */
export function useStartOverInterview(): {
  startOver: (gameId: string) => Promise<{ ok: boolean }>;
} {
  const client = useCrucibleClient();
  const queryClient = useQueryClient();
  return {
    startOver: async (gameId) => {
      const result = await client.invoke('build-game:start-over', { gameId });
      void queryClient.invalidateQueries({
        queryKey: projectQueryKey(client, ['build-game:interview-status', gameId]),
      });
      void queryClient.invalidateQueries({
        queryKey: projectQueryKey(client, ['build-game:get-interview-state', gameId]),
      });
      return result;
    },
  };
}

/**
 * Imperative helper for an Edit click in the review modal. Calls
 * `build-game:set-answer` (which writes the durable outbox + will
 * dispatch into the SDK turn once PR #5 lands the chat-message
 * route) and invalidates cached state so the renderer re-reads
 * once the skill has applied the edit.
 */
export function useSetInterviewAnswer(): {
  setAnswer: (args: {
    gameId: string;
    agent: string;
    questionId: string;
    answer: string;
  }) => Promise<{ ok: boolean; clientEditId: string }>;
} {
  const client = useCrucibleClient();
  const queryClient = useQueryClient();
  return {
    setAnswer: async (args) => {
      const result = await client.invoke('build-game:set-answer', args);
      void queryClient.invalidateQueries({
        queryKey: projectQueryKey(client, ['build-game:interview-status', args.gameId]),
      });
      void queryClient.invalidateQueries({
        queryKey: projectQueryKey(client, ['build-game:get-interview-state', args.gameId]),
      });
      return result;
    },
  };
}
